import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Avatar,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Launch as LaunchIcon
} from '@mui/icons-material';

const VideoGuideSection = ({ 
  title = "Featured Video Tutorials",
  currentVideoId,
  videos = [
    {
      id: 1,
      title: "Quick Start Guide for Costing Wizard",
      duration: "05:16",
      videoId: "NDb5ASZ2t2c",
      thumbnail: "/images/guide/create_new_project.png"
    },
    {
      id: 2,
      title: "Example Project Explanation in Costing Wizard",
      duration: "04:06",
      videoId: "2HUjQRB3aIM",
      thumbnail: "/images/guide/add_item_step_1.png"
    },
    {
      id: 3,
      title: "New in CostingWizard: Import Invoices and Quotes from Excel with AI",
      duration: "5:39",
      videoId: "JeJJ4RmI87c",
      thumbnail: "/images/guide/project_details.png"
    },
    {
      id: 4,
      title: "How to Change Currency",
      duration: "0:52",
      videoId: "6FtXdSnRmBA",
      thumbnail: "/images/guide/save_new_chapter.png"
    }
  ],
  showAllCoursesText = "View All Video Courses",
  onVideoClick,
  onShowAllClick,
  maxVideos = 6
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleVideoClick = (video) => {
    if (onVideoClick) {
      onVideoClick(video);
    } else {
      // Default behavior: open YouTube video in new tab
      const youtubeUrl = `https://www.youtube.com/watch?v=${video.videoId}`;
      window.open(youtubeUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleShowAllClick = () => {
    if (onShowAllClick) {
      onShowAllClick();
    } else {
      // Default behavior: open YouTube channel
      window.open('https://www.youtube.com/@CostingWizardIL', '_blank', 'noopener,noreferrer');
    }
  };

  const displayVideos = videos.slice(0, maxVideos);

  return (
    <Card 
      elevation={3} 
      sx={{ 
        maxWidth: { xs: '100%', sm: 400, md: 400 }, 
        width: '100%',
        borderRadius: 3,
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
      }}
    >
      <CardContent sx={{ p: 0 }}>
        {/* Header */}
        <Box 
          sx={{ 
            p: { xs: 2, sm: 3 }, 
            pb: { xs: 1.5, sm: 2 },
            background: `linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)`,
            color: 'white'
          }}
        >
          <Typography 
            variant="h6" 
            component="h2" 
            sx={{ 
              fontWeight: 'bold',
              textAlign: 'center',
              fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' }
            }}
          >
            {title}
          </Typography>
        </Box>

        {/* Video List */}
        <List sx={{ py: 0 }}>
          {displayVideos.map((video, index) => {
            const isActive = currentVideoId === video.videoId;
            return (
              <ListItem
                key={video.id}
                onClick={() => handleVideoClick(video)}
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  borderBottom: index < displayVideos.length - 1 ? '1px solid #f0f0f0' : 'none',
                  backgroundColor: isActive ? '#e3f2fd' : 'transparent',
                  '&:hover': {
                    backgroundColor: isActive ? '#bbdefb' : '#f8f9fa',
                    transform: 'translateX(-2px)',
                  },
                  py: { xs: 1, sm: 1.5 },
                  px: { xs: 1.5, sm: 2 }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      background: isActive 
                        ? `linear-gradient(45deg, #4caf50, #45a049)`
                        : `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                      fontSize: '0.875rem',
                      fontWeight: 'bold'
                    }}
                  >
                    {isActive ? '▶' : index + 1}
                  </Avatar>
                </ListItemIcon>
                
                <ListItemText
                  sx={{ ml: 2 }}
                  primary={
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: isActive ? 600 : 500,
                        color: isActive ? '#1976d2' : theme.palette.text.primary,
                        lineHeight: 1.4,
                        fontSize: isMobile ? '0.875rem' : '0.95rem'
                      }}
                    >
                      {video.title}
                    </Typography>
                  }
                />
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1}}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: '0.8rem',
                      fontWeight: 500,
                      minWidth: 'fit-content'
                    }}
                  >
                    {video.duration}
                  </Typography>
                  <PlayArrowIcon 
                    sx={{ 
                      color: theme.palette.primary.main, 
                      fontSize: '1.2rem'
                    }} 
                  />
                </Box>
              </ListItem>
            );
          })}
        </List>

        {/* See All Button */}
        <Box sx={{ p: { xs: 1.5, sm: 2 }, pt: 1 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleShowAllClick}
            sx={{
              borderRadius: 2,
              py: { xs: 1, sm: 1.5 },
              fontWeight: 'bold',
              textTransform: 'none',
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              '& .MuiButton-startIcon': {
                marginRight: '12px !important',
                marginLeft: '0 !important',
              },
              '& .MuiSvgIcon-root': {
                marginRight: '12px !important',
              },
              '&:hover': {
                backgroundColor: theme.palette.primary.main,
                color: 'white',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            <LaunchIcon sx={{ fontSize: '1.2rem' }} />
            <span style={{ marginLeft: '8px' }}>{showAllCoursesText}</span>
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default VideoGuideSection; 